import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Environment, Lightformer } from '@react-three/drei';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { Building } from './Building';
import { Site } from './Site';
import { wingsOf } from './model3d';

const CameraRig = ({ mode, size, W, D, resetKey, compassRef }) => {
  const { camera, controls, size: vp } = useThree();
  const target = useRef(null);
  useEffect(() => {
    const s = size;
    const aspect = vp.width / Math.max(vp.height, 1);
    const planH = (Math.max(D / 2, W / (2 * aspect)) / Math.tan((19 * Math.PI) / 180)) * 1.4 + 14;
    const pos = mode === 'plan' ? [0, planH, 0.001] : mode === 'section' ? [s * 1.15, s * 0.45, s * 0.25] : [s * 0.9, s * 0.72, s * 1.0];
    target.current = new THREE.Vector3(...pos);
  }, [mode, size, W, D, resetKey]); // eslint-disable-line
  useFrame((_, delta) => {
    if (target.current) {
      const f = 1 - Math.exp(-Math.min(delta, 0.25) * 7);
      camera.position.lerp(target.current, f);
      if (controls) { controls.target.lerp(new THREE.Vector3(0, 0, 0), f); controls.update(); }
      if (camera.position.distanceTo(target.current) < 0.3) { camera.position.copy(target.current); if (controls) controls.update(); target.current = null; }
    }
    if (controls && compassRef.current) compassRef.current.style.transform = `rotate(${controls.getAzimuthalAngle()}rad)`;
  });
  return null;
};

const Bridge = ({ apiRef }) => {
  const { scene, gl } = useThree();
  useEffect(() => {
    apiRef.current = {
      exportPNG: () => gl.domElement.toDataURL('image/png'),
      exportGLB: () => new Promise((resolve, reject) => {
        new GLTFExporter().parse(scene, (res) => resolve(new Blob([res], { type: 'model/gltf-binary' })), reject, { binary: true, onlyVisible: true });
      }),
    };
  }, [scene, gl, apiRef]);
  return null;
};

export const Scene3D = ({ model, mode, show, highlight, floorLimit, sectionPos, selectedRoom, onSelectRoom, showDims, markMode, resetKey, compassRef, apiRef }) => {
  const { width: W, depth: D } = model.footprint;
  const size = Math.max(W, D, 12);
  const wings = useMemo(() => wingsOf(model), [model]);
  const clip = useMemo(() => (mode === 'section' ? [new THREE.Plane(new THREE.Vector3(-1, 0, 0), (sectionPos - 0.5) * W)] : []), [mode, sectionPos, W]);
  return (
    <Canvas
      shadows
      dpr={[1, 2]}
      camera={{ position: [size * 0.9, size * 0.72, size * 1.0], fov: 38, near: 0.1, far: size * 20 }}
      gl={{ preserveDrawingBuffer: true, antialias: true }}
      onCreated={({ gl }) => { gl.localClippingEnabled = true; }}
      onPointerMissed={() => onSelectRoom(null)}
      data-testid="viewer3d-canvas"
    >
      <color attach="background" args={['#1a1918']} />
      <fog attach="fog" args={['#1a1918', size * 2.6, size * 6]} />
      <hemisphereLight args={['#d6dde8', '#3b2f25', 0.55]} />
      <directionalLight position={[size * 0.75, size * 0.85, size * 0.45]} intensity={2.6} color="#ffe0bd" castShadow shadow-bias={-0.0004} shadow-normalBias={0.04}
        shadow-mapSize={[4096, 4096]} shadow-camera-left={-size * 1.1} shadow-camera-right={size * 1.1} shadow-camera-top={size * 1.1} shadow-camera-bottom={-size * 1.1} shadow-camera-far={size * 5} />
      <directionalLight position={[-size, size * 0.4, -size * 0.8]} intensity={0.45} color="#F26B1F" />
      <Environment resolution={256} frames={1}>
        <Lightformer form="rect" intensity={2.2} color="#ffd9b0" position={[6, 5, 4]} scale={[12, 6, 1]} />
        <Lightformer form="rect" intensity={1.1} color="#b9c6d8" position={[-6, 6, -4]} scale={[14, 6, 1]} />
        <Lightformer form="rect" intensity={0.5} color="#F26B1F" position={[0, 1, -8]} scale={[20, 2, 1]} />
        <Lightformer form="rect" intensity={0.8} color="#ffffff" position={[0, 10, 0]} rotation-x={Math.PI / 2} scale={[20, 20, 1]} />
      </Environment>
      <Site W={W} D={D} size={size} wings={wings} />
      <Suspense fallback={null}>
        <Building model={model} show={show} highlight={highlight} clip={clip} floorLimit={floorLimit}
          selectedRoom={selectedRoom} onSelectRoom={onSelectRoom} markMode={markMode} showDims={showDims} />
      </Suspense>
      <OrbitControls makeDefault enableDamping dampingFactor={0.08} maxPolarAngle={Math.PI / 2.05} minDistance={size * 0.25} maxDistance={size * 4} />
      <CameraRig mode={mode} size={size} W={W} D={D} resetKey={resetKey} compassRef={compassRef} />
      <Bridge apiRef={apiRef} />
    </Canvas>
  );
};
